import { usePaletteSync } from "./hooks/usePaletteSync";
import { ConnectionStatus } from "@workspace/mobile";
import React, { Suspense, useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Page } from "./components/Layout/Page/Page";
import { PatchRadixForWebView } from "./lib/radix-webview-fix";

import { Wifi, WifiOff } from "lucide-react";

import { useStartup } from "./hooks/useStartup";

import { PageLoader } from "./components/Layout/PageLoader/PageLoader";
import { useNotificationsContext } from "./hooks/useNotificationsContext";
import { ToastOptions } from "./types/toast";

import "./app.scss";
import { ErrorBoundary } from "./components/Pages/Error/ErrorBoundary";
import ErrorPage from "./components/Pages/Error/ErrorPage";
import {
  reportError,
  resetAfterError
} from "./components/Pages/Error/ErrorPageController";
import { useDeviceContext } from "./context/DeviceContext";
import { claimConnectionToast } from "./utils/connectionStatusToast";
import { isStandaloneTabletLayout } from "./utils/device";
import { isBrowser } from "./utils/environment";
import { getColourScheme } from "./utils/nativeUtils/customActions";

import { useAdoptionTelemetry } from "./hooks/useAdoptionTelemetry";
import { useAppContext } from "./hooks/useAppContext";
import { useExternalLinkInterceptor } from "./hooks/useExternalLinkInterceptor";

import { useGoogleAnalytics } from "./hooks/useGoogleAnalytics";
import { useIntercom } from "./hooks/useIntercom";
import { useWootric } from "./hooks/useWootric";
import useManageAPIVenueData from "./hooks/useManageAPIVenueData";
import useManageOrgData from "./hooks/useManageOrgData";
import { useOrganisationDataQuery } from "./hooks/useOrganisationDataQuery";
import { useTelemetry } from "./hooks/useTelemetry";
import { useUserContext } from "./hooks/useUserContext";
import { lazyWithRetry } from "./utils/lazyWithRetry";

// Route page components are code-split into their own on-demand chunks via
// lazyWithRetry (a React.lazy wrapper) so they are excluded from the initial
// bundle and only fetched when their route is first visited. The existing
// <Suspense> boundary around <Routes> renders <PageLoader /> while a chunk
// loads; lazyWithRetry retries a failed chunk fetch and reloads once to
// recover from a stale chunk after a deploy rather than leaving the fallback
// spinning on a cold cache.
const CookiePolicyPage = lazyWithRetry(() =>
  import("./pages/cookie-policy").then((m) => ({ default: m.CookiePolicyPage }))
);
const CustomersPage = lazyWithRetry(() =>
  import("./pages/customers").then((m) => ({ default: m.CustomersPage }))
);
const CustomerDetailsPage = lazyWithRetry(() =>
  import("./pages/customers").then((m) => ({ default: m.CustomerDetailsPage }))
);
const CustomerCreateRoute = lazyWithRetry(() =>
  import("./pages/customers").then((m) => ({ default: m.CustomerCreateRoute }))
);
const HomePage = lazyWithRetry(() =>
  import("./pages/dashboard").then((m) => ({ default: m.HomePage }))
);
const DebugPage = lazyWithRetry(() =>
  import("./pages/debug").then((m) => ({ default: m.DebugPage }))
);
const DiaryPage = lazyWithRetry(() =>
  import("./pages/diary").then((m) => ({ default: m.DiaryPage }))
);
const EnquiriesPage = lazyWithRetry(() =>
  import("./pages/enquiries").then((m) => ({ default: m.EnquiriesPage }))
);
const SettingsPage = lazyWithRetry(() =>
  import("./pages/settings").then((m) => ({ default: m.SettingsPage }))
);
const SoonPage = lazyWithRetry(() =>
  import("./pages/soon").then((m) => ({ default: m.SoonPage }))
);
const UserPreferencesPage = lazyWithRetry(() =>
  import("./pages/user-preferences/UserPreferencesPage").then((m) => ({
    default: m.UserPreferencesPage
  }))
);
const StoragePage = lazyWithRetry(() =>
  import("./pages/storage").then((m) => ({ default: m.StoragePage }))
);
const AccessibilityTestPage = lazyWithRetry(() =>
  import("./components/eds/AccessibilityTestPage").then((m) => ({
    default: m.AccessibilityTestPage
  }))
);

const ReportsPage = React.lazy(() => import("./pages/reports/ReportsPage"));

function App() {
  // Initialise authentication and user context
  const { status } = useStartup();
  // React Router setup
  const location = useLocation();
  // Toast notification context
  const notificationsContext = useNotificationsContext();

  // Organisation data management
  const { loadOrganisationData } = useManageOrgData();

  // App API Data
  useManageAPIVenueData();

  const { connectionStatus, setTheme } = useDeviceContext();

  // User and organisation context for Intercom
  const { user } = useUserContext();
  const { venueGroupConfig } = useAppContext();

  // Initialise Intercom widget (Collins Digital Assistant)
  useIntercom({
    user,
    venueGroupConfig,
    isAuthenticated: status === "success"
  });

  // Initialise Wootric NPS survey widget (external users only)
  useWootric({
    user,
    venueGroupConfig,
    isAuthenticated: status === "success"
  });

  // Intercept clicks on external links (e.g. from Intercom or other
  // third-party widgets) so they open in the device browser rather
  // than navigating the webview.
  useExternalLinkInterceptor();

  // CRITICAL: Apply theme immediately on page load to prevent race condition
  React.useEffect(() => {
    const applyThemeImmediately = async () => {
      try {
        const storageTheme = await getColourScheme();

        // Apply theme to DOM immediately
        document.body.setAttribute("data-theme", storageTheme);
        // Drop the pre-paint canvas colour set in index.html; the stylesheet owns it now.
        document.documentElement.style.removeProperty("background-color");
        document.body.style.removeProperty("background-color");
        if (storageTheme === "Dark") {
          document.documentElement.classList.add("dark");
        } else {
          document.documentElement.classList.remove("dark");
        }
      } catch (error) {
        console.error("Failed to apply theme immediately:", error);
      }
    };
    applyThemeImmediately();
  }, []);

  usePaletteSync();

  useTelemetry();
  useAdoptionTelemetry();
  useGoogleAnalytics();

  // Load organisation data after startup is complete
  useOrganisationDataQuery(status, loadOrganisationData);

  const initTheme = async () => {
    // Check local storage for previously set theme - Added when changed in settings toggle
    const storageTheme = await getColourScheme();
    // Apply theme
    const currentTheme = storageTheme;
    setTheme(currentTheme);
  };

  const configMode = async () => {
    // Update for CSS styling based on browser or mobile
    const mode = isBrowser() ? "browser" : "mobile";
    document.body.setAttribute("data-mode", mode);
  };

  useEffect(() => {
    // Initialize theme immediately to prevent race condition on reload
    const initializeTheme = async () => {
      await initTheme();
      configMode();
    };
    initializeTheme();
  }, []);

  useEffect(() => {
    if (!claimConnectionToast(connectionStatus)) {
      return;
    }

    // Toast notification for connection status change.
    // "unknown" is filtered by claimConnectionToast above; keep the map
    // exhaustive for every status that can announce a toast.
    type AnnouncedConnectionStatus = Exclude<ConnectionStatus, "unknown">;
    const messageMap: Record<AnnouncedConnectionStatus, React.ReactElement> = {
      internet: (
        <div>
          Connection established <Wifi />
        </div>
      ),
      none: (
        <div>
          Working offline <WifiOff />
        </div>
      ),
      local: (
        <div>
          Limited connectivity <Wifi />
        </div>
      ),
      constrainedInternet: (
        <div>
          Slow connection <Wifi />
        </div>
      )
    };

    if (connectionStatus === "unknown") {
      return;
    }

    notificationsContext.addNotification({
      heading: messageMap[connectionStatus],
      autoClose: 2000,
      toastType: "information"
    });
  }, [connectionStatus]);

  useEffect(() => {
    const handleToastNotification = (event: Event): void => {
      notificationsContext.addNotification(
        (event as CustomEvent<ToastOptions>).detail
      );
    };

    window.addEventListener("toastNotification", handleToastNotification);

    return () => {
      window.removeEventListener("toastNotification", handleToastNotification);
    };
  }, [notificationsContext]);

  return (
    // Catch any JSX render errors and display an error page
    <ErrorBoundary
      FallbackComponent={ErrorPage}
      onError={reportError}
      onReset={resetAfterError}
    >
      <>
        {/* Global patch for Radix UI positioning in WebView */}
        <PatchRadixForWebView />
        <div
          className={`App App--load-${status}${isStandaloneTabletLayout() ? " App--tablet" : ""}`}
        >
          <Suspense fallback={<PageLoader />}>
            <Routes location={location}>
              <Route
                path="/bookings/:bookingId"
                element={
                  <Page title="Booking Details">
                    <DiaryPage />
                  </Page>
                }
              />
              <Route
                path="/sites/:venueId/:tab"
                element={
                  <Page title="Venue Diary">
                    <DiaryPage />
                  </Page>
                }
              />
              <Route
                path="/enquiries"
                element={
                  <Page title="Enquiries">
                    <EnquiriesPage />
                  </Page>
                }
              />
              <Route
                path="/diary"
                element={
                  <Page title="Diary">
                    <DiaryPage />
                  </Page>
                }
              />
              <Route
                path="/customers"
                element={
                  <Page title="Customers">
                    <CustomersPage />
                  </Page>
                }
              />
              <Route
                path="/customers/new"
                element={
                  <Page title="New customer">
                    <CustomerCreateRoute />
                  </Page>
                }
              />
              <Route
                path="/customers/:customerId"
                element={
                  <Page title="Customer">
                    <CustomerDetailsPage />
                  </Page>
                }
              />
              <Route
                path="/storage"
                element={
                  <Page title="Storage">
                    <StoragePage />
                  </Page>
                }
              />
              <Route
                path="/debug"
                element={
                  <Page title="Debug">
                    <DebugPage />
                  </Page>
                }
              />
              <Route
                path="/settings"
                element={
                  <Page title="Settings">
                    <SettingsPage />
                  </Page>
                }
              />
              <Route
                path="/user-preferences"
                element={
                  <Page title="User Preferences">
                    <UserPreferencesPage />
                  </Page>
                }
              />
              <Route
                path="/accessibility-test"
                element={
                  <Page title="Accessibility Test">
                    <AccessibilityTestPage />
                  </Page>
                }
              />
              <Route
                path="/soon"
                element={
                  <Page title="Coming Soon">
                    <SoonPage />
                  </Page>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <Page title="Dashboard">
                    <HomePage />
                  </Page>
                }
              />
              <Route
                path="/cookie-policy"
                element={
                  <Page title="Cookie Policy">
                    <CookiePolicyPage />
                  </Page>
                }
              />
              <Route
                path="/reports"
                element={
                  <Page title="Reports">
                    <ReportsPage />
                  </Page>
                }
              />
              <Route
                path="/reports/:venueId/overview"
                element={
                  <Page title="Reports">
                    <ReportsPage />
                  </Page>
                }
              />
              <Route
                path="/reports/:venueId/monthly"
                element={
                  <Page title="Reports">
                    <ReportsPage />
                  </Page>
                }
              />
              <Route
                path="/reports/:venueId/walk-in"
                element={
                  <Page title="Reports">
                    <ReportsPage />
                  </Page>
                }
              />
              <Route
                path="/reports/:venueId/booking-velocity"
                element={
                  <Page title="Reports">
                    <ReportsPage />
                  </Page>
                }
              />
              <Route
                path="/reports/:reportType"
                element={
                  <Page title="Reports">
                    <ReportsPage />
                  </Page>
                }
              />
              <Route path="/" element={<Navigate to="/diary" replace />} />
              <Route
                path="*"
                element={
                  <Page title="Coming Soon">
                    <SoonPage />
                  </Page>
                }
              />
            </Routes>
          </Suspense>
        </div>
      </>
    </ErrorBoundary>
  );
}

export default App;
